import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useRef, useState } from 'react';

import {
	Icon,
	IconAttention,
	IconCheckmark,
	IconTime,
	IconTimetable,
	IconWarningStrong,
} from '@flixbus/honeycomb-icons-react';
import { Tag, Tooltip, useSmartPosition } from '@flixbus/honeycomb-react';

import { SIDEBAR_NAMES } from 'common/activeElements/sidebars/sidebarsNames';
import WorkAssignmentDetailsTooltip from 'common/WorkAssignmentDetailsTooltip';
import { useActiveElements } from 'global/ActiveElements/useActiveElements';
import { getTagAppearanceProp } from 'utils/assigmentStatusToTagVariant';

import styles from './styles.module.css';

const statusMap = {
	created: {
		icon: () => <Icon InlineIcon={IconTime} size={3} />,
		text: 'Created',
	},
	pending: {
		icon: () => <Icon InlineIcon={IconTime} size={3} />,
		text: 'Pending',
	},
	planned: {
		icon: () => <Icon InlineIcon={IconTimetable} size={3} />,
		text: 'Planned',
	},
	actual: {
		icon: () => <Icon InlineIcon={IconTime} size={3} />,
		text: 'Actual',
	},
	confirmed: {
		icon: () => <Icon InlineIcon={IconCheckmark} size={3} />,
		text: 'Confirmed',
		appearance: 'success',
	},
	rejected: {
		icon: () => <Icon InlineIcon={IconAttention} size={3} />,
		text: 'Rejected',
		appearance: 'danger',
	},
};

const getFirstValue = (value) => (Array.isArray(value) ? value[0] : value);

const normalizeStation = (station) => {
	if (!station) return null;

	return {
		...station,
		name: station.name ?? station.fullname ?? station.code,
	};
};

const buildFullAssignmentData = ({
	assigment,
	rideId,
	line,
	tripNumber,
	departure,
	stationList,
}) => {
	const stations = stationList.map(normalizeStation).filter(Boolean);
	const fromStation = stations[0] ?? normalizeStation(assigment.fromStation);
	const toStation = stations[stations.length - 1] ?? normalizeStation(assigment.toStation);

	return {
		crewMember: assigment.crewMember ?? null,
		job: getFirstValue(assigment.job) ?? null,
		run: getFirstValue(assigment.run) ?? null,
		sequenceGroupId: assigment.sequenceGroupId,
		status: assigment.status ?? null,
		workAssignmentSequences: [
			{
				extraActivities: assigment.extraActivities ?? [],
				fromStation,
				fromTimestamp: assigment.fromTimestamp ?? fromStation?.departure ?? null,
				line: { code: line },
				ride: {
					departure,
					id: rideId,
					lineCode: line,
					stations,
					tripNumber,
				},
				sequenceGroupId: assigment.sequenceGroupId,
				status: assigment.status ?? null,
				toStation,
				toTimestamp: assigment.toTimestamp ?? toStation?.arrival ?? null,
			},
		],
	};
};

const WorkAssigmentTag = ({
	assigment,
	rideId,
	line,
	tripNumber,
	departure,
	timezone,
	hostRef = null,
	useHost = false,
}) => {
	const active = useActiveElements();
	const targetInnerRef = useRef();
	const balloonInnerRef = useRef();
	const [isTooltipActive, setIsTooltipActive] = useState(false);
	const [smartPosition, smartAlignment] = useSmartPosition({
		active: isTooltipActive,
		targetRef: useHost ? hostRef : targetInnerRef,
		elementRef: useHost ? hostRef : balloonInnerRef,
	});

	const stationList =
		assigment.stations ?? [assigment?.fromStation, assigment?.toStation].filter(Boolean);
	const crewMemberId = assigment?.crewMember?.id ?? null;
	const sequenceGroupId = assigment?.sequenceGroupId ?? null;
	const isGap = assigment.isGap === true;
	const breakdown = assigment.breakdown ?? null;
	const breakdownStationId = breakdown?.station?.id ?? null;
	const segmentKey = `${rideId}:${stationList
		.map((station) => station?.id)
		.filter(Boolean)
		.join('-')}`;
	const canOpenSidebar = Boolean(sequenceGroupId || (isGap && stationList.length >= 2));
	const isSidebarShown =
		active.active?.name === SIDEBAR_NAMES.waDetails &&
		active.active?.props?.currentRideId === rideId &&
		(sequenceGroupId
			? active.active?.props?.sequenceGroupId === sequenceGroupId
			: active.active?.props?.segmentKey === segmentKey);

	const tagVariant = isGap
		? statusMap.rejected
		: (statusMap[assigment?.status] ?? statusMap.rejected);
	const hasNoAssignment =
		isGap || assigment?.crewMember?.fullName == null || assigment?.crewMember?.fullName === '';

	const shortActivityDetails = {
		route: stationList.map((station) => ({
			...station,
			name: station.fullname,
			plannedDeparture: station.departure,
			plannedArrival: station.arrival,
		})),
		crewMember: assigment.crewMember || {},
		status: assigment.status,
		rideId,
		line: { code: line },
		tripNumber,
		runs: assigment?.run || [],
		jobs: assigment?.job || [],
	};

	const tooltipContent = (
		<WorkAssignmentDetailsTooltip
			timezone={timezone}
			assignment={shortActivityDetails}
			isShort={true}
			isSegment={true}
		/>
	);

	const renderStations = (stations) => {
		let pastBreakdown = false;
		const hasMatchingStation =
			breakdownStationId && stations.some((station) => station.id === breakdownStationId);
		const crossLastStation = breakdown && !hasMatchingStation;

		return stations.map((station, index) => {
			if (hasMatchingStation && station.id === breakdownStationId) {
				pastBreakdown = true;
			}

			const isStrikethrough = pastBreakdown || (crossLastStation && index === stations.length - 1);

			return (
				<span key={station.id} className={classNames({ [styles.strikethrough]: isStrikethrough })}>
					{station.code}
					{index !== stations.length - 1 && ` → `}
				</span>
			);
		});
	};

	const openSidebar = (e) => {
		e.stopPropagation();
		e.preventDefault();
		if (!canOpenSidebar) return;

		const sidebarProps = {
			crewMemberId,
			sequenceGroupId,
			currentRideId: rideId,
			breakdown: breakdown ?? null,
		};

		if (!crewMemberId || isGap) {
			sidebarProps.fullAssignmentData = buildFullAssignmentData({
				assigment,
				rideId,
				line,
				tripNumber,
				departure,
				stationList,
			});
		}

		if (isGap) {
			sidebarProps.segmentKey = segmentKey;
		}

		active.setActiveElement(SIDEBAR_NAMES.waDetails, sidebarProps);
	};

	return (
		<Tooltip
			id="tooltip-top"
			content={tooltipContent}
			innerRef={targetInnerRef}
			balloonRef={balloonInnerRef}
			size="content-fit"
			openOnHover
			extraClasses={styles.modal}
			position={smartPosition}
			alignment={smartAlignment}
			onToggle={setIsTooltipActive}
		>
			<Tag
				small
				{...getTagAppearanceProp(hasNoAssignment ? undefined : assigment?.status)}
				extraClasses={classNames(styles.stations, {
					[styles.selected]: isSidebarShown && canOpenSidebar,
					[styles.breakdown]: Boolean(breakdown),
				})}
				onClick={(e) => openSidebar(e)}
			>
				{breakdown ? <Icon InlineIcon={IconWarningStrong} size={3} /> : tagVariant.icon()}
				{renderStations(stationList)}
			</Tag>
		</Tooltip>
	);
};

const ObservedWorkAssigmentTag = observer(WorkAssigmentTag);

export default ObservedWorkAssigmentTag;
